import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import {
  type NarrationManifest,
  type NarrationManifestEntry,
  type StaticNarrationPromptKey,
} from '@course-authoring/shared-types';
import {
  createNarrationMediaAdapter,
  type NarrationMediaAdapter,
  type NarrationPlaybackStatus,
} from './narrationMediaAdapter';
import type {
  ClickExpandNarrationTarget,
  ClickExpandTargetRegistration,
  NarrationEvent,
  NarrationEventHandler,
} from './narrationEvents';
import {
  attachMeaningfulInteractionListeners,
  isClickExpandTopicControlOf,
} from './narrationManualIntent';
import { scrollNarrationBlockIntoView, type NarrationBlockScroller } from './narrationScroll';
import { type NarrationAvailability, useNarrationScrollCoordinator } from './hooks/useNarrationScrollCoordinator';
import { useClickExpandTargetRegistry } from './hooks/useClickExpandTargetRegistry';
import {
  INTERACTION_PROMPT_KEYS,
  assertNeverNarrationEvent,
  assertNeverPlanKind,
  createActivePlan,
  describePlaybackPurpose,
  getActiveVideoDisposition,
  hasReadyEntry,
  isClickExpandPlan,
  isDuplicateQuestionOutcome,
  isQuestionNarrationBlockType,
  isSkippableNarrationGap,
  isSupportedNarrationBlock,
  nextAutoAdvanceTopicIndex,
  unexpectedNarrationGapStatus,
  planKindForBlock,
  questionSubmitPromptKey,
  readyLessonTitleEntry,
  topicSeamKey,
  type AcknowledgementEvent,
  type ActiveNarrationPlan,
  type ActiveVideoObservation,
  type ComparisonEvent,
  type MapEvent,
  type NarrationOrderedBlock,
  type NarrationSequenceStep,
  type PlaybackPurpose,
  type QuestionNarrationState,
  type QuestionOutcomeEvent,
  type StandaloneVideoEvent,
  type TopicEvent,
} from './narrationPlans';

export type { NarrationOrderedBlock } from './narrationPlans';

export type LearnerToolbarPanel = 'profile' | 'language' | 'speed' | null;

export const AUDIO_SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
export type AudioSpeed = (typeof AUDIO_SPEEDS)[number];

export const DEFAULT_AUDIO_SPEED: AudioSpeed = 1;

interface ActiveNarrationPlayback {
  planToken: number;
  playbackToken: number;
  purpose: PlaybackPurpose;
  blockId: string;
  url: string;
  onEnded: () => void;
  onFailed?: () => void;
}

type TitlePreludeState = 'pending' | 'waiting-for-video' | 'complete' | 'skipped';
type TitlePreludeContinuation = 'start-first-block' | 'advance-past-first-block';

export interface AudioControlsContextValue {
  audioAvailable: boolean;
  audioOn: boolean;
  toggleAudio: () => void;

  playbackStatus: NarrationPlaybackStatus;
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
  togglePlaying: () => void;

  speed: AudioSpeed;
  setSpeed: (speed: AudioSpeed) => void;

  openPanel: LearnerToolbarPanel;
  setOpenPanel: (panel: LearnerToolbarPanel) => void;

  currentBlockId: string | null;
  setCurrentBlockId: (id: string | null, source?: 'passive' | 'manual') => void;
  registerNarrationAvailability: (prepare: NarrationAvailability) => () => void;
  /**
   * Click-Expand renderers report their target per block. Pass the released
   * target as `previousTarget` on unmount so only that renderer's entry is
   * cleared; a stale unregister never removes a newer target.
   */
  registerClickExpandTarget: ClickExpandTargetRegistration;
  getClickExpandTarget: (blockId: string) => ClickExpandNarrationTarget | null;
  handleNarrationEvent: NarrationEventHandler;
}

const noop = (): void => undefined;

/** Cancels a narration-opened Accordion reveal that has not run yet, when a plan moves past it. */
function cancelNarrationReveal(plan: ActiveNarrationPlan | null): void {
  plan?.clickExpandTarget?.cancelPendingReveal();
}

/**
 * True when `event` targets a topic control of the narrated Click-Expand block.
 * Narration reacts to that gesture's semantic topic event, not to the press itself.
 */
function isNarratedTopicGesture(plan: ActiveNarrationPlan | null, event: Event): boolean {
  return plan !== null && isClickExpandPlan(plan) && isClickExpandTopicControlOf(event.target, plan.blockId);
}

/** Suspends a Click-Expand plan: topic events stop driving it and only Play resumes narration. */
function suspendClickExpandPlan(plan: ActiveNarrationPlan | null): void {
  if (!plan || !isClickExpandPlan(plan) || plan.suspended) return;
  plan.suspended = true;
  cancelNarrationReveal(plan);
}

const DEFAULT_VALUE: AudioControlsContextValue = {
  audioAvailable: false,
  audioOn: false,
  toggleAudio: noop,
  playbackStatus: 'idle',
  isPlaying: false,
  play: noop,
  pause: noop,
  togglePlaying: noop,
  speed: DEFAULT_AUDIO_SPEED,
  setSpeed: noop,
  openPanel: null,
  setOpenPanel: noop,
  currentBlockId: null,
  setCurrentBlockId: noop,
  registerNarrationAvailability: () => noop,
  registerClickExpandTarget: noop,
  getClickExpandTarget: () => null,
  handleNarrationEvent: noop,
};

const AudioControlsContext = createContext<AudioControlsContextValue>(DEFAULT_VALUE);

export interface AudioControlsProviderProps {
  children: ReactNode;
  lessonId?: string | null;
  blocks?: readonly NarrationOrderedBlock[];
  narration?: NarrationManifest;
  /** Test seam; production creates one dependency-free HTMLAudioElement adapter. */
  createMediaAdapter?: () => NarrationMediaAdapter;
  /** Test seam; production animates the player's own scroll viewport. */
  scrollBlockIntoView?: NarrationBlockScroller;
}

export function AudioControlsProvider({
  children,
  lessonId,
  blocks = [],
  narration,
  createMediaAdapter: createAdapter = createNarrationMediaAdapter,
  scrollBlockIntoView = scrollNarrationBlockIntoView,
}: AudioControlsProviderProps): React.ReactElement {
  const adapterRef = useRef<NarrationMediaAdapter | null>(null);
  if (adapterRef.current === null) adapterRef.current = createAdapter();
  const adapter = adapterRef.current;

  const availabilityRef = useRef<NarrationAvailability | null>(null);
  const pendingAdvanceRef = useRef(false);
  const cancelPendingAdvanceRef = useRef<() => void>(noop);
  const registerNarrationAvailability = useCallback((prepare: NarrationAvailability) => {
    availabilityRef.current = prepare;
    return () => {
      if (availabilityRef.current !== prepare) return;
      availabilityRef.current = null;
      if (pendingAdvanceRef.current) cancelPendingAdvanceRef.current();
    };
  }, []);
  const prepareBlock = useCallback<NarrationAvailability>((id, ready, interrupted) => {
    if (availabilityRef.current) return availabilityRef.current(id, ready, interrupted);
    ready();
    return noop;
  }, []);
  const {
    registerClickExpandTarget,
    getClickExpandTarget,
    claimForPlan: claimClickExpandTargetForPlan,
  } = useClickExpandTargetRegistry();
  const planTokenRef = useRef(0);
  const playbackTokenRef = useRef(0);
  const activePlanRef = useRef<ActiveNarrationPlan | null>(null);
  const activePlaybackRef = useRef<ActiveNarrationPlayback | null>(null);
  const titlePreludeStateRef = useRef<TitlePreludeState>('pending');
  const titlePreludeContinuationRef = useRef<TitlePreludeContinuation>('start-first-block');
  const activeVideosRef = useRef<Map<string, ActiveVideoObservation>>(new Map());
  const questionStatesRef = useRef<Map<string, QuestionNarrationState>>(new Map());
  const startBlockRef = useRef<(
    blockId: string,
    shouldCenter?: boolean,
    trigger?: 'automatic' | 'manual',
  ) => boolean>(() => false);
  const startNarrationAtBlockRef = useRef<(
    blockId: string,
    shouldScroll?: boolean,
    trigger?: 'automatic' | 'manual',
  ) => boolean>(() => false);
  const advanceFromBlockRef = useRef<(blockId: string) => void>(() => undefined);
  const advanceAfterPlanRef = useRef<(planToken: number) => void>(() => undefined);
  const continueClickExpandRef = useRef<(plan: ActiveNarrationPlan) => void>(noop);

  const [audioOn, setAudioOn] = useState(false);
  const audioOnRef = useRef(audioOn);
  audioOnRef.current = audioOn;
  const [playbackStatus, setPlaybackStatus] = useState<NarrationPlaybackStatus>(
    adapter.getStatus(),
  );
  const [speed, setSpeedState] = useState<AudioSpeed>(DEFAULT_AUDIO_SPEED);
  const speedRef = useRef<AudioSpeed>(speed);
  speedRef.current = speed;
  const [openPanel, setOpenPanel] = useState<LearnerToolbarPanel>(null);
  const [currentBlockId, setCurrentBlockIdState] = useState<string | null>(null);
  const currentBlockIdRef = useRef<string | null>(currentBlockId);
  currentBlockIdRef.current = currentBlockId;
  const blocksRef = useRef(blocks);
  blocksRef.current = blocks;
  const narrationRef = useRef(narration);
  narrationRef.current = narration;
  const lessonIdRef = useRef(lessonId);
  lessonIdRef.current = lessonId;
  const audioAvailable = useMemo(
    () => (
      readyLessonTitleEntry(narration, lessonId) !== undefined
      || blocks.some((block) => {
        if (!isSupportedNarrationBlock(block)) return false;
        const blockEntries = narration?.blocks[block.id];
        if (hasReadyEntry(blockEntries)) return true;
        if (isQuestionNarrationBlockType(block.type)) {
          const promptKey = questionSubmitPromptKey();
          return narration?.staticPrompts?.[promptKey]?.status === 'ready';
        }
        const promptKeys = INTERACTION_PROMPT_KEYS[block.type] ?? [];
        if (promptKeys.some((key) => narration?.staticPrompts?.[key]?.status === 'ready')) {
          return true;
        }
        return block.type === 'map' && hasReadyEntry(narration?.mapRegions);
      })
    ),
    [blocks, lessonId, narration],
  );

  const setCurrentBlockId = useCallback((id: string | null, source?: 'passive' | 'manual'): void => {
    if (source === 'passive' && audioOnRef.current &&
      (activePlanRef.current || activePlaybackRef.current || pendingAdvanceRef.current)) return;
    if (source === 'manual' && id !== currentBlockIdRef.current && pendingAdvanceRef.current) {
      cancelPendingAdvanceRef.current();
    }
    currentBlockIdRef.current = id;
    setCurrentBlockIdState(id);
  }, []);

  const {
    scrollBlockWhenAvailable,
    cancelActiveScroll,
  } = useNarrationScrollCoordinator(scrollBlockIntoView, prepareBlock);

  const stopActivePlayback = useCallback((): void => {
    playbackTokenRef.current += 1;
    activePlaybackRef.current = null;
    adapter.pause();
  }, [adapter]);

  /**
   * Ends the active plan and its playback before narration moves on to a block.
   * Unlike `invalidateNarration`, it leaves pending scrolls and advances alone.
   */
  const retireActivePlan = useCallback((): void => {
    cancelNarrationReveal(activePlanRef.current);
    planTokenRef.current += 1;
    activePlanRef.current = null;
    stopActivePlayback();
  }, [stopActivePlayback]);

  const closeSpeedPanel = useCallback((): void => {
    setOpenPanel((panel) => (panel === 'speed' ? null : panel));
  }, []);

  /**
   * Abandons whatever narration was in flight: any pending scroll, the active
   * plan, and the active playback. Every path that stops narration goes
   * through here, so a newly added resource is released everywhere at once
   * rather than in whichever path its author remembered.
   */
  const invalidateNarration = useCallback((): void => {
    pendingAdvanceRef.current = false;
    cancelActiveScroll();
    cancelNarrationReveal(activePlanRef.current);
    planTokenRef.current += 1;
    playbackTokenRef.current += 1;
    activePlanRef.current = null;
    activePlaybackRef.current = null;
  }, [cancelActiveScroll]);

  cancelPendingAdvanceRef.current = invalidateNarration;

  const haltPlan = useCallback((expectedPlanToken?: number, pauseMedia = true): void => {
    if (expectedPlanToken !== undefined && planTokenRef.current !== expectedPlanToken) return;
    invalidateNarration();
    if (pauseMedia) adapter.pause();
  }, [adapter, invalidateNarration]);

  /** Turns narration off, whether the learner asked or it became unavailable. */
  const disableAudio = useCallback((): void => {
    audioOnRef.current = false;
    setAudioOn(false);
    if (titlePreludeStateRef.current === 'waiting-for-video') {
      titlePreludeStateRef.current = 'pending';
      titlePreludeContinuationRef.current = 'start-first-block';
    }
    invalidateNarration();
    closeSpeedPanel();
    adapter.release();
  }, [adapter, closeSpeedPanel, invalidateNarration]);

  const pause = useCallback((): void => {
    haltPlan(undefined, true);
  }, [haltPlan]);

  const handlePlaybackFailure = useCallback((
    planToken: number,
    playbackToken: number,
    pauseMedia: boolean,
  ): void => {
    const activePlayback = activePlaybackRef.current;
    if (
      activePlayback?.planToken !== planToken
      || activePlayback.playbackToken !== playbackToken
    ) return;
    // Raw browser detail only: a 404 and an unsupported container share
    // MEDIA_ERR_SRC_NOT_SUPPORTED, so the cause is for a human to judge.
    console.warn('[Narration] Playback failed', {
      blockId: activePlayback.blockId,
      step: describePlaybackPurpose(activePlayback.purpose),
      url: activePlayback.url,
      failure: adapter.getLastError(),
    });
    if (!activePlayback.onFailed) {
      haltPlan(planToken, pauseMedia);
      return;
    }
    playbackTokenRef.current += 1;
    activePlaybackRef.current = null;
    if (pauseMedia) adapter.pause();
    activePlayback.onFailed();
  }, [adapter, haltPlan]);

  const playEntry = useCallback((
    planToken: number,
    blockId: string,
    entry: NarrationManifestEntry | undefined,
    purpose: PlaybackPurpose,
    onEnded: () => void,
    onFailed?: () => void,
  ): void => {
    if (planTokenRef.current !== planToken || !audioOnRef.current) return;
    if (!entry || entry.status !== 'ready') {
      // `empty` is an authored decision, not a gap worth reporting.
      if (entry?.status !== 'empty') {
        console.warn('[Narration] Skipped entry', {
          blockId,
          step: describePlaybackPurpose(purpose),
          status: entry?.status ?? 'absent',
        });
      }
      onEnded();
      return;
    }

    const playbackToken = ++playbackTokenRef.current;
    activePlaybackRef.current = {
      planToken,
      playbackToken,
      purpose,
      blockId,
      url: entry.url,
      onEnded,
      onFailed,
    };
    void adapter.restart(entry.url, speedRef.current, playbackToken).then(() => {
      const activePlayback = activePlaybackRef.current;
      if (
        activePlayback?.planToken === planToken &&
        activePlayback.playbackToken === playbackToken &&
        adapter.getStatus() === 'paused'
      ) {
        handlePlaybackFailure(planToken, playbackToken, false);
      }
    }).catch(() => {
      const activePlayback = activePlaybackRef.current;
      if (
        activePlayback?.planToken === planToken &&
        activePlayback.playbackToken === playbackToken
      ) {
        handlePlaybackFailure(planToken, playbackToken, true);
      }
    });
  }, [adapter, handlePlaybackFailure]);

  const playSequence = useCallback((
    planToken: number,
    steps: readonly NarrationSequenceStep[],
    onComplete: () => void,
    failForward = false,
  ): void => {
    const playAt = (index: number): void => {
      const activePlan = activePlanRef.current;
      if (activePlan?.token !== planToken || !audioOnRef.current) return;
      const step = steps[index];
      if (!step) {
        onComplete();
        return;
      }
      const continueSequence = (): void => playAt(index + 1);
      playEntry(
        planToken,
        activePlan.blockId,
        step.entry,
        step.purpose,
        continueSequence,
        failForward ? continueSequence : undefined,
      );
    };
    playAt(0);
  }, [playEntry]);

  /**
   * Plays one Click-Expand topic seam. A seam that is not `ready` completes
   * silently (playEntry logs missing or absent entries) and the loop continues
   * at once. The topic stays counted; the next topic auto-advance opens replaces it.
   */
  const playTopicSeam = useCallback((plan: ActiveNarrationPlan, topicIndex: number): void => {
    const entry = narrationRef.current?.blocks[plan.blockId]?.[topicSeamKey(topicIndex)];
    plan.phase = 'topic';
    playEntry(
      plan.token,
      plan.blockId,
      entry,
      { kind: 'topic', topicIndex },
      () => {
        if (activePlanRef.current?.token !== plan.token) return;
        continueClickExpandRef.current(plan);
      },
    );
  }, [playEntry]);

  /**
   * Auto-advance step. Opens the lowest-indexed topic that is neither opened nor
   * passed over and whose seam is `ready`, then reads it. Unreadable topics are
   * passed over without opening. When none remains, the plan advances.
   */
  const continueClickExpand = useCallback((plan: ActiveNarrationPlan): void => {
    if (activePlanRef.current?.token !== plan.token || plan.suspended) return;
    const target = claimClickExpandTargetForPlan(plan);
    if (!target) {
      haltPlan(plan.token, true);
      return;
    }
    const nextIndex = nextAutoAdvanceTopicIndex(
      plan.topicIds.length,
      target.getState().openedTopicIndices,
      plan.skippedTopics,
    );
    if (nextIndex === null) {
      advanceAfterPlanRef.current(plan.token);
      return;
    }

    const entry = narrationRef.current?.blocks[plan.blockId]?.[topicSeamKey(nextIndex)];
    if (entry?.status !== 'ready') {
      plan.skippedTopics.add(nextIndex);
      playEntry(
        plan.token,
        plan.blockId,
        entry,
        { kind: 'topic', topicIndex: nextIndex },
        () => continueClickExpandRef.current(plan),
      );
      return;
    }

    cancelNarrationReveal(plan);
    if (!target.openTopic(nextIndex)) {
      haltPlan(plan.token, true);
      return;
    }
    playTopicSeam(plan, nextIndex);
  }, [haltPlan, playEntry, playTopicSeam, claimClickExpandTargetForPlan]);
  continueClickExpandRef.current = continueClickExpand;

  const playTextComparisonSide = useCallback((
    plan: ActiveNarrationPlan,
    side: 1 | 2,
    includeInitialTail: boolean,
  ): void => {
    if (activePlanRef.current?.token !== plan.token) return;
    plan.activeSide = side;
    plan.phase = 'comparison-option';
    playEntry(
      plan.token,
      plan.blockId,
      narrationRef.current?.blocks[plan.blockId]?.[`option:${side}`],
      { kind: 'comparison-option', side },
      () => {
        if (activePlanRef.current?.token !== plan.token) return;
        plan.countedSides.add(side);
        if (!includeInitialTail) {
          plan.phase = 'waiting-for-side';
          if (plan.countedSides.size === 2) advanceAfterPlanRef.current(plan.token);
          return;
        }
        plan.phase = 'comparison-initial-tail';
        playSequence(plan.token, [
          {
            entry: narrationRef.current?.blocks[plan.blockId]?.[`cue:${side}`],
            purpose: { kind: 'comparison-step', step: `cue:${side}`, side },
          },
          {
            entry: narrationRef.current?.staticPrompts?.['resume.on_other_option'],
            purpose: { kind: 'comparison-step', step: 'resume.on_other_option' },
          },
        ], () => {
          if (activePlanRef.current?.token === plan.token) plan.phase = 'waiting-for-side';
        });
      },
    );
  }, [playEntry, playSequence]);

  const playVideoComparisonSide = useCallback((
    plan: ActiveNarrationPlan,
    side: 1 | 2,
  ): void => {
    if (activePlanRef.current?.token !== plan.token) return;
    plan.activeSide = side;
    plan.phase = 'comparison-video-prompt';
    const steps: NarrationSequenceStep[] = [{
      entry: narrationRef.current?.blocks[plan.blockId]?.[`option:${side}`],
      purpose: { kind: 'comparison-option', side },
    }];
    if (!plan.countedSides.has(side)) {
      steps.push({
        entry: narrationRef.current?.staticPrompts?.['resume.after_video'],
        purpose: { kind: 'comparison-step', step: 'resume.after_video', side },
      });
    }
    playSequence(plan.token, steps, () => {
      if (activePlanRef.current?.token === plan.token) plan.phase = 'waiting-for-video';
    });
  }, [playSequence]);

  const playQuestionInitialSequence = useCallback((
    plan: ActiveNarrationPlan,
  ): void => {
    if (activePlanRef.current?.token !== plan.token) return;
    const blockEntries = narrationRef.current?.blocks[plan.blockId];
    const block = blocksRef.current.find((candidate) => candidate.id === plan.blockId);
    const promptKey = questionSubmitPromptKey();
    plan.phase = 'question-initial';
    playSequence(plan.token, [
      { entry: blockEntries?.title, purpose: { kind: 'block-step', step: 'title' } },
      { entry: blockEntries?.question, purpose: { kind: 'block-step', step: 'question' } },
      // NA-383: Single Choice options are their own bank-owned seam, so a block
      // that owns its question text never carries bank content in its audio.
      // Packages built before the split have no `options` entry (their
      // `question` audio still includes the options), and it is skipped.
      ...(block?.type === 'single-choice-answer'
        ? [{ entry: blockEntries?.options, purpose: { kind: 'block-step', step: 'options' } as const }]
        : []),
      {
        entry: narrationRef.current?.staticPrompts?.[promptKey],
        purpose: { kind: 'block-step', step: promptKey },
      },
    ], () => {
      if (activePlanRef.current?.token === plan.token) {
        plan.phase = 'question-pre-submit-wait';
      }
    });
  }, [playSequence]);

  const playQuestionOutcomeSequence = useCallback((
    plan: ActiveNarrationPlan,
    block: NarrationOrderedBlock,
    state: QuestionNarrationState,
  ): void => {
    if (activePlanRef.current?.token !== plan.token) return;
    const blockEntries = narrationRef.current?.blocks[plan.blockId];
    const { event } = state;

    if (event.outcome === 'incorrect') {
      state.stage = 'retry-wait';
      plan.phase = 'question-retry-wait';
      return;
    }

    if (event.presentation === 'hidden-results') {
      state.stage = 'complete';
      plan.phase = 'question-complete';
      advanceAfterPlanRef.current(plan.token);
      return;
    }

    if (event.presentation === 'visible-results') {
      state.stage = 'post-submit';
      plan.phase = 'question-post-submit';
      const steps: NarrationSequenceStep[] = [];
      if (block.type === 'workplace-color-spectrum' && blockEntries?.explanation?.status !== 'empty') {
        steps.push({
          entry: blockEntries?.explanation,
          purpose: { kind: 'block-step', step: 'explanation' },
        });
      }
      playSequence(plan.token, [
        ...steps,
        {
          entry: narrationRef.current?.staticPrompts?.['pause.review_results'],
          purpose: { kind: 'block-step', step: 'pause.review_results' },
        },
      ], () => {
        if (activePlanRef.current?.token !== plan.token) return;
        state.stage = 'final-review';
        plan.phase = 'question-final-review';
      }, true);
      return;
    }

    state.stage = 'post-submit';
    plan.phase = 'question-post-submit';
    playSequence(plan.token, [{
      entry: blockEntries?.explanation,
      purpose: { kind: 'block-step', step: 'explanation' },
    }], () => {
      if (activePlanRef.current?.token !== plan.token) return;
      state.stage = 'complete';
      plan.phase = 'question-complete';
      advanceAfterPlanRef.current(plan.token);
    }, true);
  }, [playSequence]);

  const startQuestionPlan = useCallback((
    plan: ActiveNarrationPlan,
    block: NarrationOrderedBlock,
    questionState: QuestionNarrationState | null,
    trigger: 'automatic' | 'manual',
  ): void => {
    if (!questionState) {
      playQuestionInitialSequence(plan);
      return;
    }
    if (trigger === 'manual') {
      if (questionState.stage === 'retry-wait') {
        playQuestionInitialSequence(plan);
      } else if (questionState.stage === 'post-submit') {
        playQuestionOutcomeSequence(plan, block, questionState);
      } else {
        advanceAfterPlanRef.current(plan.token);
      }
      return;
    }
    if (questionState.event.outcome === 'incorrect') {
      questionState.stage = 'retry-wait';
      plan.phase = 'question-retry-wait';
    } else {
      questionState.stage = 'complete';
      plan.phase = 'question-complete';
      advanceAfterPlanRef.current(plan.token);
    }
  }, [playQuestionInitialSequence, playQuestionOutcomeSequence]);

  const startContinuousPlan = useCallback((plan: ActiveNarrationPlan): void => {
    plan.phase = 'continuous';
    playEntry(
      plan.token,
      plan.blockId,
      narrationRef.current?.blocks[plan.blockId]?.content,
      { kind: 'continuous-content' },
      () => advanceAfterPlanRef.current(plan.token),
    );
  }, [playEntry]);

  const startVideoPlan = useCallback((plan: ActiveNarrationPlan): void => {
    plan.phase = 'video-intro';
    playSequence(plan.token, [
      {
        entry: narrationRef.current?.blocks[plan.blockId]?.content,
        purpose: { kind: 'block-step', step: 'content' },
      },
      {
        entry: narrationRef.current?.staticPrompts?.['resume.after_video'],
        purpose: { kind: 'block-step', step: 'resume.after_video' },
      },
    ], () => {
      if (activePlanRef.current?.token === plan.token) plan.phase = 'waiting-for-video';
    });
  }, [playSequence]);

  const startAcknowledgementPlan = useCallback((
    plan: ActiveNarrationPlan,
    kind: 'policy-acknowledgement' | 'electronic-signature' | 'link',
  ): void => {
    const promptKey: StaticNarrationPromptKey = kind === 'policy-acknowledgement'
      ? 'resume.after_document_acknowledgement'
      : kind === 'electronic-signature'
      ? 'resume.after_acknowledgement'
      : 'resume.on_link_click';
    plan.phase = 'acknowledgement-intro';
    playSequence(plan.token, [
      {
        entry: narrationRef.current?.blocks[plan.blockId]?.content,
        purpose: { kind: 'block-step', step: 'content' },
      },
      {
        entry: narrationRef.current?.staticPrompts?.[promptKey],
        purpose: { kind: 'block-step', step: promptKey },
      },
    ], () => {
      if (activePlanRef.current?.token === plan.token) {
        plan.phase = 'waiting-for-acknowledgement';
      }
    });
  }, [playSequence]);

  const startClickExpandPlan = useCallback((plan: ActiveNarrationPlan): void => {
    plan.phase = 'click-expand-intro';
    // Capture the renderer mounted at plan start, so a replacement during intro ends the plan
    // instead of being adopted. A renderer that registers only later is adopted on first lookup.
    plan.clickExpandTarget = getClickExpandTarget(plan.blockId);
    playEntry(
      plan.token,
      plan.blockId,
      narrationRef.current?.blocks[plan.blockId]?.intro,
      { kind: 'block-step', step: 'intro' },
      () => {
        if (activePlanRef.current?.token !== plan.token) return;
        const target = claimClickExpandTargetForPlan(plan);
        if (!target) {
          // Without the renderer the plan cannot read or open topics. Pause, do not advance.
          haltPlan(plan.token, true);
          return;
        }
        const { openTopicIndex } = target.getState();
        if (openTopicIndex === null) {
          continueClickExpandRef.current(plan);
          return;
        }
        playTopicSeam(plan, openTopicIndex);
      },
    );
  }, [claimClickExpandTargetForPlan, getClickExpandTarget, haltPlan, playEntry, playTopicSeam]);

  const startComparisonPlan = useCallback((
    plan: ActiveNarrationPlan,
    kind: 'comparison-text' | 'comparison-video',
  ): void => {
    plan.phase = 'comparison-intro';
    playSequence(plan.token, [{
      entry: narrationRef.current?.blocks[plan.blockId]?.intro,
      purpose: { kind: 'comparison-step', step: 'intro' },
    }], () => {
      if (kind === 'comparison-text') playTextComparisonSide(plan, 1, true);
      else playVideoComparisonSide(plan, 1);
    });
  }, [playSequence, playTextComparisonSide, playVideoComparisonSide]);

  const startMapPlan = useCallback((plan: ActiveNarrationPlan): void => {
    plan.phase = 'map-intro';
    playSequence(plan.token, [
      {
        entry: narrationRef.current?.blocks[plan.blockId]?.content,
        purpose: { kind: 'block-step', step: 'content' },
      },
      {
        entry: narrationRef.current?.staticPrompts?.['resume.on_region_selection'],
        purpose: { kind: 'block-step', step: 'resume.on_region_selection' },
      },
    ], () => {
      if (activePlanRef.current?.token === plan.token) plan.phase = 'waiting-for-region';
    });
  }, [playSequence]);

  const startBlock = useCallback((
    blockId: string,
    shouldScroll = true,
    trigger: 'automatic' | 'manual' = 'automatic',
  ): boolean => {
    const block = blocksRef.current.find((candidate) => candidate.id === blockId);

    retireActivePlan();

    if (!block) return false;
    if (block.type === 'basic-content-b') {
      advanceFromBlockRef.current(blockId);
      return true;
    }
    if (!isSupportedNarrationBlock(block)) return false;
    const kind = planKindForBlock(block);
    if (!kind) return false;

    setCurrentBlockId(blockId);
    const positionBlock = (onSettled: () => void, onInterrupted?: () => void): void => {
      if (shouldScroll) scrollBlockWhenAvailable(blockId, onSettled, onInterrupted);
      else onSettled();
    };

    const questionState = kind === 'question'
      ? questionStatesRef.current.get(blockId) ?? null
      : null;
    const plan = createActivePlan(planTokenRef.current, block, kind, questionState);
    activePlanRef.current = plan;

    const videoDisposition = getActiveVideoDisposition(activeVideosRef.current, blockId, kind);
    if (videoDisposition === 'own-video-wait') {
      const activeVideo = activeVideosRef.current.get(blockId);
      if (activeVideo?.kind === 'comparison') plan.activeSide = activeVideo.side;
      plan.phase = 'waiting-for-video';
      positionBlock(noop);
      return true;
    }
    if (videoDisposition === 'blocked') {
      activePlanRef.current = null;
      positionBlock(noop);
      return false;
    }

    // Narration waits for the scroll: the learner sees the block arrive before
    // hearing it. A stale settle is dropped by the plan-token check, exactly
    // like a stale playback callback.
    const beginPlan = (): void => {
      if (activePlanRef.current?.token !== plan.token || !audioOnRef.current) return;
      switch (kind) {
        case 'question':
          startQuestionPlan(plan, block, questionState, trigger);
          return;
        case 'continuous':
          startContinuousPlan(plan);
          return;
        case 'video':
          startVideoPlan(plan);
          return;
        case 'policy-acknowledgement':
        case 'electronic-signature':
        case 'link':
          startAcknowledgementPlan(plan, kind);
          return;
        case 'accordion':
        case 'tabs':
          startClickExpandPlan(plan);
          return;
        case 'comparison-text':
        case 'comparison-video':
          startComparisonPlan(plan, kind);
          return;
        case 'map':
          startMapPlan(plan);
          return;
        default:
          assertNeverPlanKind(kind);
      }
    };

    // Learner takeover during the scroll leaves narration paused on this
    // block; Play picks it up again per the usual rules.
    positionBlock(beginPlan, () => haltPlan(plan.token, false));
    return true;
  }, [
    haltPlan,
    scrollBlockWhenAvailable,
    setCurrentBlockId,
    startAcknowledgementPlan,
    startClickExpandPlan,
    startComparisonPlan,
    startContinuousPlan,
    startMapPlan,
    startQuestionPlan,
    startVideoPlan,
    stopActivePlayback,
  ]);
  startBlockRef.current = startBlock;

  /**
   * Entry point for learner-initiated starts (Play, narration toggle). Unlike
   * `startBlock`, this may play only the lesson-title prelude first, without
   * creating a block plan, and then hand off to the block.
   */
  const startNarrationAtBlock = useCallback((
    blockId: string,
    shouldScroll = true,
    trigger: 'automatic' | 'manual' = 'automatic',
  ): boolean => {
    if (titlePreludeStateRef.current === 'waiting-for-video') {
      if (activeVideosRef.current.size > 0) return true;
      titlePreludeStateRef.current = 'pending';
    }
    if (titlePreludeStateRef.current !== 'pending') {
      return startBlock(blockId, shouldScroll, trigger);
    }

    const firstBlock = blocksRef.current[0];
    if (!firstBlock || firstBlock.id !== blockId) {
      titlePreludeStateRef.current = 'skipped';
      titlePreludeContinuationRef.current = 'start-first-block';
      return startBlock(blockId, shouldScroll, trigger);
    }

    const activeLessonId = lessonIdRef.current;
    const titleEntry = readyLessonTitleEntry(narrationRef.current, activeLessonId);
    if (!titleEntry) {
      titlePreludeStateRef.current = 'skipped';
      titlePreludeContinuationRef.current = 'start-first-block';
      return startBlock(blockId, shouldScroll, trigger);
    }

    const firstBlockKind = planKindForBlock(firstBlock);
    const videoDisposition = firstBlockKind
      ? getActiveVideoDisposition(activeVideosRef.current, firstBlock.id, firstBlockKind)
      : activeVideosRef.current.size > 0 ? 'blocked' : 'none';
    if (videoDisposition !== 'none') {
      titlePreludeStateRef.current = 'waiting-for-video';
      titlePreludeContinuationRef.current = videoDisposition === 'own-video-wait'
        ? 'advance-past-first-block'
        : 'start-first-block';
      haltPlan(undefined, true);
      return true;
    }

    // Read at callback time: a title that ended after any of these moved on
    // must not start a block the learner is no longer positioned on.
    const preludeStillCurrent = (preludePlanToken: number): boolean => (
      // Not cancelled by pause, toggle-off, or a newer plan.
      planTokenRef.current === preludePlanToken
      && audioOnRef.current
      // Same lesson entry that started the title.
      && lessonIdRef.current === activeLessonId
      // Learner has not scrolled the current block away from the first block.
      && currentBlockIdRef.current === firstBlock.id
      // Block order still begins where the title was attached.
      && blocksRef.current[0]?.id === firstBlock.id
      // A lesson change reset, or a later start, has not already resolved it.
      && titlePreludeStateRef.current === 'pending'
    );

    // The title is a provider-owned phase tied to the real first block as its
    // current surface. It intentionally does not create a block plan, because
    // a protected first block must still be allowed to run this prelude.
    haltPlan(undefined, true);
    const preludePlanToken = planTokenRef.current;
    playEntry(
      preludePlanToken,
      firstBlock.id,
      titleEntry,
      { kind: 'lesson-title' },
      () => {
        if (!preludeStillCurrent(preludePlanToken)) return;
        titlePreludeStateRef.current = 'complete';
        const continuation = titlePreludeContinuationRef.current;
        titlePreludeContinuationRef.current = 'start-first-block';
        if (continuation === 'advance-past-first-block') {
          advanceFromBlockRef.current(firstBlock.id);
        } else {
          startBlockRef.current(firstBlock.id, shouldScroll, trigger);
        }
      },
    );
    return true;
  }, [haltPlan, playEntry, startBlock]);
  startNarrationAtBlockRef.current = startNarrationAtBlock;

  const advanceFromBlock = useCallback((blockId: string): void => {
    const orderedBlocks = blocksRef.current;
    const activeIndex = orderedBlocks.findIndex((block) => block.id === blockId);
    const activeBlock = orderedBlocks[activeIndex];

    if (activeIndex < 0 || activeBlock?.type === 'lesson-complete') return;
    for (let index = activeIndex + 1; index < orderedBlocks.length; index += 1) {
      const nextBlock = orderedBlocks[index];
      if (!nextBlock) continue;
      const skippable = isSkippableNarrationGap(nextBlock, narrationRef.current);
      if (skippable) {
        const unexpectedStatus = unexpectedNarrationGapStatus(nextBlock, narrationRef.current);
        if (unexpectedStatus !== null) {
          console.warn('[Narration] Skipped block with no playable content', {
            blockId: nextBlock.id,
            step: 'content',
            status: unexpectedStatus,
          });
        }
        // Even a trailing run of silent blocks must become available.
        if (index < orderedBlocks.length - 1) continue;
      }
      pendingAdvanceRef.current = true;
      const scrollPlanToken = planTokenRef.current;
      scrollBlockWhenAvailable(nextBlock.id, () => {
        if (planTokenRef.current !== scrollPlanToken || !audioOnRef.current) return;
        pendingAdvanceRef.current = false;
        if (skippable) return;
        setCurrentBlockId(nextBlock.id);
        if (isSupportedNarrationBlock(nextBlock)) startBlockRef.current(nextBlock.id, false);
      }, () => {
        pendingAdvanceRef.current = false;
        cancelPendingAdvanceRef.current();
      });
      return;
    }
  }, [scrollBlockWhenAvailable, setCurrentBlockId]);
  advanceFromBlockRef.current = advanceFromBlock;

  const advanceAfterPlan = useCallback((planToken: number): void => {
    const plan = activePlanRef.current;
    if (!audioOnRef.current || plan?.token !== planToken) return;
    const completedBlockId = plan.blockId;

    retireActivePlan();
    advanceFromBlockRef.current(completedBlockId);
  }, [retireActivePlan]);
  advanceAfterPlanRef.current = advanceAfterPlan;

  const play = useCallback((): void => {
    const blockId = currentBlockIdRef.current;
    if (!audioOnRef.current || blockId === null) {
      haltPlan(undefined, true);
      return;
    }
    const activeVideo = activeVideosRef.current.get(blockId);
    const activePlan = activePlanRef.current;
    if (
      activeVideo &&
      activePlan?.blockId === blockId &&
      activePlan.phase === 'waiting-for-video'
    ) return;
    startNarrationAtBlock(blockId, true, 'manual');
  }, [haltPlan, startNarrationAtBlock]);

  const activateQuestionOutcome = useCallback((
    block: NarrationOrderedBlock,
    state: QuestionNarrationState,
  ): void => {
    if (getActiveVideoDisposition(activeVideosRef.current, block.id, 'question') === 'blocked') {
      haltPlan(undefined, true);
      return;
    }
    planTokenRef.current += 1;
    stopActivePlayback();
    const plan = createActivePlan(planTokenRef.current, block, 'question', state);
    activePlanRef.current = plan;
    playQuestionOutcomeSequence(plan, block, state);
  }, [haltPlan, playQuestionOutcomeSequence, stopActivePlayback]);

  const handleVideoObservation = useCallback((
    event: NarrationEvent,
    plan: ActiveNarrationPlan | null,
  ): void => {
    switch (event.type) {
      case 'video-playback-started':
        activeVideosRef.current.set(event.blockId, {
          kind: 'standalone',
          blockId: event.blockId,
        });
        return;
      case 'video-playback-completed': {
        const activeVideo = activeVideosRef.current.get(event.blockId);
        if (activeVideo?.kind === 'standalone') activeVideosRef.current.delete(event.blockId);
        return;
      }
      case 'comparison-side-selected':
        activeVideosRef.current.delete(event.blockId);
        return;
      case 'comparison-video-playback-started': {
        const matchesVisiblePlan = plan?.blockId !== event.blockId
          || plan.kind !== 'comparison-video'
          || plan.activeSide === event.side;
        if (matchesVisiblePlan) {
          activeVideosRef.current.set(event.blockId, {
            kind: 'comparison',
            blockId: event.blockId,
            side: event.side,
          });
        }
        return;
      }
      case 'comparison-video-playback-completed': {
        const activeVideo = activeVideosRef.current.get(event.blockId);
        if (activeVideo?.kind === 'comparison' && activeVideo.side === event.side) {
          activeVideosRef.current.delete(event.blockId);
        }
        return;
      }
      default:
        return;
    }
  }, []);

  const handleQuestionOutcome = useCallback((event: QuestionOutcomeEvent): void => {
    const existingState = questionStatesRef.current.get(event.blockId);
    if (isDuplicateQuestionOutcome(existingState, event, activePlanRef.current)) return;
    const state: QuestionNarrationState = {
      event,
      stage: event.outcome === 'incorrect' ? 'retry-wait' : 'post-submit',
    };
    questionStatesRef.current.set(event.blockId, state);
    if (!audioOnRef.current || currentBlockIdRef.current !== event.blockId) return;
    const block = blocksRef.current.find((candidate) => candidate.id === event.blockId);
    if (!block || planKindForBlock(block) !== 'question') return;
    activateQuestionOutcome(block, state);
  }, [activateQuestionOutcome]);

  const handleStandaloneVideoEvent = useCallback((
    event: StandaloneVideoEvent,
    plan: ActiveNarrationPlan,
  ): void => {
    if (plan.kind !== 'video') return;
    if (event.type === 'video-playback-started') {
      stopActivePlayback();
      plan.phase = 'waiting-for-video';
      return;
    }
    advanceAfterPlanRef.current(plan.token);
  }, [stopActivePlayback]);

  const handleAcknowledgementEvent = useCallback((
    event: AcknowledgementEvent,
    plan: ActiveNarrationPlan,
  ): void => {
    const matchesPlan =
      (plan.kind === 'policy-acknowledgement' && event.acknowledgement === 'document')
      || (plan.kind === 'electronic-signature'
        && event.acknowledgement === 'electronic-signature')
      || (plan.kind === 'link' && event.acknowledgement === 'link');
    if (matchesPlan) advanceAfterPlanRef.current(plan.token);
  }, []);

  const handleTopicEvent = useCallback((
    event: TopicEvent,
    plan: ActiveNarrationPlan,
  ): void => {
    if (!isClickExpandPlan(plan)) return;
    // Suspended by a learner interaction: the click only changes renderer state,
    // and the next Play reads that state.
    if (plan.suspended) return;
    // The plan's renderer was unregistered or replaced, so the plan is obsolete:
    // end it without starting a seam or advancing.
    if (claimClickExpandTargetForPlan(plan) === null) {
      haltPlan(plan.token, true);
      return;
    }
    // Paused outside narration (media keys, OS interruption) while this plan's
    // playback is loaded: the paused seam no longer matches the screen. End the
    // plan and unload its audio so a system resume cannot continue it; the click
    // only opens the topic and the next Play reads on-screen state. Narration's
    // own stops clear the active playback before pausing, so they never get here.
    if (adapter.getStatus() === 'paused' && activePlaybackRef.current !== null) {
      haltPlan(plan.token, false);
      adapter.release();
      return;
    }
    if (plan.topicIds[event.topicIndex] !== event.topicId) return;
    cancelNarrationReveal(plan);
    stopActivePlayback();
    if (event.type === 'topic-selected') {
      playTopicSeam(plan, event.topicIndex);
      return;
    }
    // Closing a topic during intro or a topic seam moves on to auto-advance.
    continueClickExpandRef.current(plan);
  }, [adapter, haltPlan, playTopicSeam, claimClickExpandTargetForPlan, stopActivePlayback]);

  const handleComparisonEvent = useCallback((
    event: ComparisonEvent,
    plan: ActiveNarrationPlan,
  ): void => {
    if (event.type === 'comparison-side-selected') {
      if (plan.kind === 'comparison-text') {
        if (plan.phase === 'comparison-option' && plan.activeSide !== event.side) {
          plan.countedSides.add(plan.activeSide);
        }
        stopActivePlayback();
        playTextComparisonSide(plan, event.side, false);
      } else if (plan.kind === 'comparison-video') {
        stopActivePlayback();
        playVideoComparisonSide(plan, event.side);
      }
      return;
    }
    if (plan.kind !== 'comparison-video') return;
    if (event.type === 'comparison-video-playback-started') {
      if (event.side !== plan.activeSide) return;
      stopActivePlayback();
      plan.phase = 'waiting-for-video';
      return;
    }
    const completedVisibleSide = plan.activeSide === event.side;
    plan.countedSides.add(event.side);
    if (plan.countedSides.size === 2) {
      advanceAfterPlanRef.current(plan.token);
      return;
    }
    if (!completedVisibleSide) return;
    stopActivePlayback();
    plan.phase = 'comparison-video-complete';
    playSequence(plan.token, [
      {
        entry: narrationRef.current?.blocks[plan.blockId]?.[`cue:${event.side}`],
        purpose: { kind: 'comparison-step', step: `cue:${event.side}`, side: event.side },
      },
      {
        entry: narrationRef.current?.staticPrompts?.['resume.on_other_option'],
        purpose: { kind: 'comparison-step', step: 'resume.on_other_option' },
      },
    ], () => {
      if (activePlanRef.current?.token === plan.token) plan.phase = 'waiting-for-side';
    });
  }, [playSequence, playTextComparisonSide, playVideoComparisonSide, stopActivePlayback]);

  const handleMapEvent = useCallback((event: MapEvent, plan: ActiveNarrationPlan): void => {
    if (plan.kind !== 'map') return;
    if (event.type === 'map-region-closed') {
      if (plan.activeRegionId === event.regionId) advanceAfterPlanRef.current(plan.token);
      return;
    }
    if (
      event.type === 'map-region-changed'
      && plan.activeRegionId !== event.previousRegionId
    ) return;
    stopActivePlayback();
    plan.activeRegionId = event.regionId;
    plan.phase = 'map-region';
    playEntry(
      plan.token,
      plan.blockId,
      narrationRef.current?.mapRegions?.[event.regionId],
      { kind: 'map-region', regionId: event.regionId },
      () => {
        if (activePlanRef.current?.token === plan.token) {
          plan.phase = 'waiting-for-region-close';
        }
      },
    );
  }, [playEntry, stopActivePlayback]);

  const handleNarrationEvent = useCallback<NarrationEventHandler>((event): void => {
    const plan = activePlanRef.current;
    const titleWasPlaying = activePlaybackRef.current?.purpose.kind === 'lesson-title';
    handleVideoObservation(event, plan);
    if (
      titleWasPlaying
      && (event.type === 'video-playback-started'
        || event.type === 'comparison-video-playback-started')
    ) {
      const firstBlock = blocksRef.current[0];
      const firstBlockKind = firstBlock ? planKindForBlock(firstBlock) : null;
      const videoDisposition = firstBlock && firstBlockKind
        ? getActiveVideoDisposition(activeVideosRef.current, firstBlock.id, firstBlockKind)
        : 'blocked';
      titlePreludeStateRef.current = 'waiting-for-video';
      titlePreludeContinuationRef.current = videoDisposition === 'own-video-wait'
        ? 'advance-past-first-block'
        : 'start-first-block';
      haltPlan(undefined, true);
      return;
    }
    if (
      titlePreludeStateRef.current === 'waiting-for-video'
      && activeVideosRef.current.size === 0
      && (event.type === 'video-playback-completed'
        || event.type === 'comparison-video-playback-completed')
    ) {
      const blockId = currentBlockIdRef.current;
      titlePreludeStateRef.current = 'pending';
      if (audioOnRef.current && blockId !== null) {
        startNarrationAtBlockRef.current(blockId);
      }
      return;
    }
    if (event.type === 'question-outcome-committed') {
      handleQuestionOutcome(event);
      return;
    }
    if (!audioOnRef.current || !plan || event.blockId !== plan.blockId) return;

    switch (event.type) {
      case 'video-playback-started':
      case 'video-playback-completed':
        handleStandaloneVideoEvent(event, plan);
        return;
      case 'acknowledged':
        handleAcknowledgementEvent(event, plan);
        return;
      case 'topic-selected':
      case 'topic-closed':
        handleTopicEvent(event, plan);
        return;
      case 'comparison-side-selected':
      case 'comparison-video-playback-started':
      case 'comparison-video-playback-completed':
        handleComparisonEvent(event, plan);
        return;
      case 'map-region-opened':
      case 'map-region-changed':
      case 'map-region-closed':
        handleMapEvent(event, plan);
        return;
      default:
        return assertNeverNarrationEvent(event);
    }
  }, [
    handleAcknowledgementEvent,
    handleComparisonEvent,
    handleMapEvent,
    handleQuestionOutcome,
    handleStandaloneVideoEvent,
    handleTopicEvent,
    handleVideoObservation,
    haltPlan,
  ]);

  useEffect(() => adapter.subscribe((nextStatus, playbackToken) => {
    setPlaybackStatus(nextStatus);
    const activePlayback = activePlaybackRef.current;
    if (
      !activePlayback ||
      activePlayback.playbackToken !== playbackToken ||
      planTokenRef.current !== activePlayback.planToken
    ) return;
    if (nextStatus === 'ended') {
      activePlaybackRef.current = null;
      activePlayback.onEnded();
    } else if (nextStatus === 'paused') {
      // Only a pause from outside narration reaches here with playback still
      // active; a pending narration reveal must not scroll the paused learner.
      cancelNarrationReveal(activePlanRef.current);
    } else if (nextStatus === 'error') {
      handlePlaybackFailure(
        activePlayback.planToken,
        activePlayback.playbackToken,
        false,
      );
    }
  }), [adapter, handlePlaybackFailure]);

  useEffect(() => {
    titlePreludeStateRef.current = 'pending';
    titlePreludeContinuationRef.current = 'start-first-block';
    invalidateNarration();
    // Lesson-scoped observations, unlike the narration state above, are only
    // meaningful for the lesson that recorded them.
    activeVideosRef.current.clear();
    questionStatesRef.current.clear();
    adapter.release();
    setCurrentBlockId(null);
    closeSpeedPanel();
  }, [adapter, closeSpeedPanel, invalidateNarration, lessonId, setCurrentBlockId]);

  useEffect(() => () => adapter.release(), [adapter]);

  useEffect(() => {
    if (audioAvailable || !audioOnRef.current) return;
    disableAudio();
  }, [audioAvailable, disableAudio]);

  const toggleAudio = useCallback((): void => {
    if (audioOnRef.current) {
      disableAudio();
      return;
    }
    if (!audioAvailable) return;

    audioOnRef.current = true;
    setAudioOn(true);
    const blockId = currentBlockIdRef.current;
    if (blockId !== null) startNarrationAtBlock(blockId);
  }, [audioAvailable, disableAudio, startNarrationAtBlock]);

  const togglePlaying = useCallback((): void => {
    if (playbackStatus === 'playing') pause();
    else play();
  }, [pause, play, playbackStatus]);

  const setSpeed = useCallback((nextSpeed: AudioSpeed): void => {
    speedRef.current = nextSpeed;
    setSpeedState(nextSpeed);
    adapter.setPlaybackRate(nextSpeed);
  }, [adapter]);

  // Learner interaction stops playback and suspends a Click-Expand plan, even
  // between seams, so a later topic click does not restart narration. A topic
  // control of the narrated block is the exception: its semantic topic event
  // follows, and narration reacts to that event rather than to the press.
  useEffect(() => attachMeaningfulInteractionListeners((event) => {
    const plan = activePlanRef.current;
    if (isNarratedTopicGesture(plan, event)) return;
    suspendClickExpandPlan(plan);
    const status = adapter.getStatus();
    if (status === 'playing' || status === 'loading') stopActivePlayback();
  }), [adapter, stopActivePlayback]);

  // A current-block change also catches manual viewport movement whose intent
  // event was not observable. Narration's own scroll sets the tracked block
  // before playback, so this does not pause programmatic advancement.
  useEffect(() => {
    const plan = activePlanRef.current;
    if (plan && currentBlockId !== null && currentBlockId !== plan.blockId) suspendClickExpandPlan(plan);
    if (playbackStatus !== 'playing' && playbackStatus !== 'loading') return;
    const playbackBlockId = activePlaybackRef.current?.blockId;
    if (playbackBlockId === undefined || currentBlockId === null) return;
    if (currentBlockId === playbackBlockId) return;
    stopActivePlayback();
  }, [currentBlockId, playbackStatus, stopActivePlayback]);

  const value = useMemo<AudioControlsContextValue>(
    () => ({
      audioAvailable,
      audioOn,
      toggleAudio,
      playbackStatus,
      isPlaying: playbackStatus === 'playing',
      play,
      pause,
      togglePlaying,
      speed,
      setSpeed,
      openPanel,
      setOpenPanel,
      currentBlockId,
      setCurrentBlockId,
      registerNarrationAvailability,
      registerClickExpandTarget,
      getClickExpandTarget,
      handleNarrationEvent,
    }),
    [
      audioAvailable,
      audioOn,
      toggleAudio,
      playbackStatus,
      play,
      pause,
      togglePlaying,
      speed,
      setSpeed,
      openPanel,
      currentBlockId,
      setCurrentBlockId,
      registerNarrationAvailability,
      registerClickExpandTarget,
      getClickExpandTarget,
      handleNarrationEvent,
    ],
  );

  return <AudioControlsContext.Provider value={value}>{children}</AudioControlsContext.Provider>;
}

export function useAudioControls(): AudioControlsContextValue {
  return useContext(AudioControlsContext);
}
